/**
 * Email-domain helpers for the workspace invite allowlist.
 *
 * Mirrors the server's normalization + validation (apps/api
 * `normalize_email_domain` and the `SetAllowedDomainsRequest` regex) so the
 * editor can reject a bad domain and de-dupe before the PUT. The backend
 * re-validates and is the source of truth; this is a UX pre-check, not the
 * authority.
 */

/** Lowercase, trim, drop a leading `@` — the canonical stored form. */
export const normalizeDomain = (value) =>
  (value || "").trim().toLowerCase().replace(/^@/, "");

// Two or more dot-separated labels ending in an alphabetic TLD, applied to the
// normalized form: "acme.com", "mail.acme.co.uk" pass; "gmail", "@acme.com",
// "a..b", "acme.1" do not. Kept in step with the API's _DOMAIN_RE.
const DOMAIN_RE =
  /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)*\.[a-z]{2,}$/;

/** Whether `value`, once normalized, is a well-formed domain. */
export const isValidDomain = (value) => DOMAIN_RE.test(normalizeDomain(value));

/**
 * Normalize + de-duplicate a domain list, order-preserving, dropping blanks.
 * Matches the server's `_clean`, so the editor shows what will actually store.
 */
export const cleanDomains = (domains) => {
  const seen = new Set();
  const out = [];
  for (const raw of domains || []) {
    const normalized = normalizeDomain(raw);
    if (normalized && !seen.has(normalized)) {
      seen.add(normalized);
      out.push(normalized);
    }
  }
  return out;
};
